/**
 * The founder apps — the single list behind every surface that names them.
 *
 * These are the standalone, no-login tools a stranger can use before they have
 * a Lev project: Idea Finder, Founder Type, Zeitgeist. They appear as cards on
 * the marketing home page and on `/founder-apps`, and as links in the marketing
 * footer and the Lev Learn footer.
 *
 * ## Why a shared constant
 *
 * Each of those surfaces used to hold its own hand-written copy of the list,
 * and the copies drifted exactly the way hand-written copies do: Zeitgeist
 * shipped as a card in July 2026 and was never added to either footer, so two
 * of the three surfaces advertised two tools while the product had three. A
 * missing footer link on a public page is invisible in review — nothing fails,
 * the page just quietly under-sells. One list means adding the fourth tool is
 * one edit, and forgetting a surface is no longer possible.
 *
 * ## Rules for anyone editing this file
 *
 * - **`title` is the analytics name.** It is passed straight to
 *   `trackFounderAppClicked({ appName })`, so renaming a tool splits its funnel
 *   in PostHog. Rename deliberately, and expect the break in continuity.
 * - **`href` must be the canonical route, not a redirect.** `/idea-finder` is
 *   the real page; `/founder/idea-matcher` is a legacy path whose page body is
 *   a `redirect("/idea-finder")`. Linking the legacy one costs every visitor an
 *   extra hop, splits inbound analytics, and drops the query string — which is
 *   how a `?src=` attribution param goes missing.
 * - **A tool that renders its own footer will link to itself.** Lev Learn is in
 *   this list and its pages carry `LearnFooter`, which would list Lev Learn
 *   under "Start something" and again as its own column. The Learn-family
 *   footers use `LEARN_FOOTER_APPS` (`@/lib/learn/nav`), this list minus Lev
 *   Learn; the marketing footer still shows all of it.
 * - **Flag-gated tools do not belong here.** The Lev Learn footer is a server
 *   component that must stay prerendered, so it cannot read a feature flag.
 *   Anything conditional (the YC application, for one) stays inline in the
 *   client surface that gates it.
 */

export interface FounderApp {
  /** Visible label in link lists, card heading, and the PostHog `appName`. */
  title: string;
  /** Canonical route — never a redirected legacy path. */
  href: string;
  /** Card eyebrow, e.g. "IDEATE". */
  tag: string;
  /** Card body copy. */
  description: string;
  /** Card icon in `/public/landing`. */
  icon: string;
}

export const FOUNDER_APPS: readonly FounderApp[] = [
  {
    title: "Idea Finder",
    href: "/idea-finder",
    tag: "IDEATE",
    description:
      "We've generated 160,000+ startup ideas through AI. Plug in your profile and get concepts custom-fit to you.",
    icon: "/landing/founder-test-icon.jpg",
  },
  {
    title: "Founder Type",
    href: "/founder/type",
    tag: "DISCOVERY",
    description:
      "Discover your entrepreneurial personality and learn how to leverage your strengths as a founder.",
    icon: "/landing/founder-test-icon2.png",
  },
  {
    title: "Lev Learn",
    href: "/learn",
    tag: "REFERENCE",
    description:
      "The concepts founders are expected to already know, explained plainly. 175 of them, free and public, from ICP to liquidation preference.",
    icon: "/landing/lev-learn-gears-icon.png",
  },
  {
    title: "Zeitgeist",
    href: "/zeitgeist",
    tag: "TRENDS",
    description:
      "A weekly trend radar. We scan the world for macro shifts creating startup opportunities, then turn each into ideas you can build.",
    icon: "/landing/founder-test-icon1.png",
  },
];
